/**
 * Authentication Context
 *
 * Provides Firebase Authentication state and methods throughout the application.
 * Implements user session management, sign-in/sign-up/sign-out functionality,
 * and automatic user profile synchronization with Firebase Realtime Database.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  type User as FirebaseUser,
} from 'firebase/auth';
import { ref, set, get, update } from 'firebase/database';
import { auth, db } from '@/lib/firebase';
import type { User } from '@/types/models';
import { setUserContext, clearUserContext } from '@/lib/sentry';
import { setAnalyticsUser, trackUserSignup, trackUserLogin } from '@/lib/analytics';

interface AuthContextType {
  currentUser: FirebaseUser | null;
  userProfile: User | null;
  loading: boolean;
  signUp: (email: string, password: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Hook to access authentication context
 * @throws Error if used outside AuthProvider
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Authentication Provider Component
 *
 * Wraps the application to provide authentication state and methods.
 * Automatically syncs user profiles with Firebase Realtime Database.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * Create or update user profile in database
   */
  async function syncUserProfile(firebaseUser: FirebaseUser): Promise<void> {
    const userRef = ref(db, `users/${firebaseUser.uid}`);

    try {
      // Check if profile exists
      const snapshot = await get(userRef);

      if (snapshot.exists()) {
        // Update last login time
        await update(userRef, {
          lastLoginAt: Date.now(),
        });

        // Load existing profile
        setUserProfile(snapshot.val() as User);
      } else {
        // Create new profile
        // Note: Firebase doesn't allow undefined values, so only include fields with values
        const newProfile: User = {
          email: firebaseUser.email || '',
          createdAt: Date.now(),
          lastLoginAt: Date.now(),
          ...(firebaseUser.displayName && { displayName: firebaseUser.displayName }),
          ...(firebaseUser.photoURL && { photoURL: firebaseUser.photoURL }),
        };

        await set(userRef, newProfile);
        setUserProfile(newProfile);
      }
    } catch (error) {
      console.error('Error syncing user profile:', error);
      throw error;
    }
  }

  /**
   * Sign up with email and password
   */
  async function signUp(email: string, password: string): Promise<void> {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    await syncUserProfile(userCredential.user);

    // Track signup
    trackUserSignup('email');
    setUserContext(userCredential.user.uid, userCredential.user.email || undefined);
    setAnalyticsUser(userCredential.user.uid);
  }

  /**
   * Sign in with email and password
   */
  async function signIn(email: string, password: string): Promise<void> {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    await syncUserProfile(userCredential.user);

    // Track login
    trackUserLogin('email');
    setUserContext(userCredential.user.uid, userCredential.user.email || undefined);
    setAnalyticsUser(userCredential.user.uid);
  }

  /**
   * Sign in with Google OAuth
   * Uses popup on desktop; falls back to redirect on mobile (popup blocked)
   */
  async function signInWithGoogle(): Promise<void> {
    const provider = new GoogleAuthProvider();
    try {
      const userCredential = await signInWithPopup(auth, provider);
      await syncUserProfile(userCredential.user);
      trackUserLogin('google');
      setUserContext(userCredential.user.uid, userCredential.user.email || undefined);
      setAnalyticsUser(userCredential.user.uid);
    } catch (error: unknown) {
      const code = (error as { code?: string }).code;
      if (code === 'auth/popup-blocked' || code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        await signInWithRedirect(auth, provider);
      } else {
        throw error;
      }
    }
  }

  /**
   * Send password reset email
   */
  async function resetPassword(email: string): Promise<void> {
    await sendPasswordResetEmail(auth, email);
  }

  /**
   * Sign out current user
   */
  async function signOut(): Promise<void> {
    await firebaseSignOut(auth);
    setUserProfile(null);

    // Clear tracking contexts
    clearUserContext();
  }

  /**
   * Listen for authentication state changes
   */
  useEffect(() => {
    // Handle return from signInWithRedirect (mobile fallback)
    getRedirectResult(auth).then((result) => {
      if (result?.user) {
        trackUserLogin('google');
        setUserContext(result.user.uid, result.user.email || undefined);
        setAnalyticsUser(result.user.uid);
      }
    }).catch((error) => {
      console.error('Google redirect error:', error);
    });

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        try {
          await syncUserProfile(user);
        } catch (error) {
          console.error('Failed to sync user profile:', error);
        }
      } else {
        setUserProfile(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value: AuthContextType = {
    currentUser,
    userProfile,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
